import * as ReactDOM from "react-dom/client";
import * as React from "react";
import "./index-tailwind.css";
import 'bootstrap';
import "./index.scss";
import "./assets/fonts/popins/stylesheet.css";
import "./assets/fonts/icons/style.css";
import 'mapbox-gl/dist/mapbox-gl.css';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

// Initialize environment configuration before anything else
import { ENABLE_SENTRY } from './config/env';

import * as Sentry from "@sentry/react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from "./App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Only when domain is not localhost
if (ENABLE_SENTRY === 'true') {
  Sentry.init({
    environment: 'production',
    attachStacktrace: true,
    dsn: "https://42dda66118e117e9a92e7b9557beb7b5@o4506740156792832.ingest.sentry.io/4506740160069632",
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/api\.affinigo\.com/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}


ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
        <div className="tw-max-w-md tw-w-full tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
          <div className="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full tw-bg-red-100 tw-mb-4">
            <svg className="tw-h-6 tw-w-6 tw-text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Une erreur inattendue s'est produite</h1>
          <div className="tw-text-gray-600 tw-mb-6 tw-space-y-2">
            <p>L'erreur a été signalée à notre équipe.</p>
            <p>Si vous êtes bloqué, vous pouvez prendre contact avec notre équipe au <strong className="tw-text-gray-900">0 800 944 665</strong>.</p>
          </div>
          <div className="tw-space-y-3">
            <button
              onClick={() => window.location.href = '/'}
              className="tw-w-full tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-blue-700"
            >
              Retour à l'accueil
            </button>
            <button
              onClick={resetError}
              className="tw-w-full tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-md hover:tw-bg-gray-300"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    )}
    beforeCapture={(scope) => {
      scope.setLevel('error');
    }}
  >
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);